import { motion, useReducedMotion } from "framer-motion";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

const BARS = [
  { h: 40, gold: false },
  { h: 65, gold: true },
  { h: 50, gold: false },
  { h: 85, gold: false },
  { h: 70, gold: true },
  { h: 95, gold: false },
  { h: 60, gold: false },
];

export default function DashboardCard() {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, rotateY: -8, rotateX: 6 }}
      animate={{
        opacity: 1,
        scale: 1,
        rotateY: -8,
        rotateX: 6,
        y: reduce ? 0 : [0, -16, 0],
      }}
      transition={{
        opacity: { duration: 0.8, delay: 0.4 },
        scale: { duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] },
        default: { duration: 0.8, delay: 0.4 },
        y: { duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1.4 },
      }}
      style={{ transformPerspective: 1400 }}
      className="absolute inset-0 m-auto h-fit w-[280px] rounded-3xl border border-line bg-surface p-5 shadow-card-lg sm:w-[320px] sm:p-6 lg:w-[360px]"
    >
      {/* Head */}
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-[13.5px] font-bold text-ink">{t("dash.title")}</h4>
        <span className="flex items-center gap-1.5 rounded-full bg-teal-soft px-2.5 py-1 text-[11px] font-bold text-teal dark:bg-teal-2/15 dark:text-teal-3">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-3 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-2" />
          </span>
          {t("dash.online")}
        </span>
      </div>

      {/* Chart */}
      <div className="mb-4 flex h-20 items-end gap-1.5 sm:h-[90px] sm:gap-[7px]">
        {BARS.map((bar, i) => (
          <motion.div
            key={i}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{
              delay: 0.9 + i * 0.08,
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1],
            }}
            style={{ height: `${bar.h}%` }}
            className={clsx(
              "flex-1 origin-bottom rounded-t-lg rounded-b-[3px]",
              bar.gold
                ? "bg-gradient-to-b from-gold-2 to-gold"
                : "bg-gradient-to-b from-teal-2 to-teal"
            )}
          />
        ))}
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-2.5">
        <Row label={t("dash.chatsLabel")} value={t("dash.chatsValue")} />
        <Row label={t("dash.convLabel")} value={t("dash.convValue")} />
        <Row label={t("dash.avgLabel")} value={t("dash.avgValue")} />
      </div>
    </motion.div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-surface-2 px-3.5 py-2.5 text-[12.5px]">
      <span className="text-ink-soft">{label}</span>
      <b className="text-[13px] font-extrabold text-teal-2">{value}</b>
    </div>
  );
}